<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useWhatsAppCallsStore } from 'dashboard/stores/whatsappCalls';

const props = defineProps({
  chat: { type: Object, required: true },
  inbox: { type: Object, default: () => ({}) },
  contact: { type: Object, default: () => ({}) },
});

const store = useStore();
const callsStore = useWhatsAppCallsStore();

const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const userToken = computed(() => currentUser.value?.pubsub_token);
const userId = computed(() => currentUser.value?.id);

const isWhatsAppCloudInbox = computed(
  () => props.inbox?.channel_type === 'Channel::Whatsapp' && props.inbox?.provider === 'whatsapp_cloud'
);

const conversationId = computed(() => props.chat?.id);
const contactId = computed(() => props.contact?.id);
const contactName = computed(() => props.contact?.name || props.contact?.phone_number);

const hasActiveCall = computed(() => callsStore.hasActiveCall);
const isCalling = computed(() => callsStore.callStatus === 'calling' || callsStore.callStatus === 'requesting_outbound');
const permissionError = computed(() => callsStore.permissionError);
const showPermissionPrompt = computed(
  () => permissionError.value && permissionError.value.contactId === contactId.value
);
const pendingPermission = computed(() => callsStore.pendingPermissionFor === contactId.value);

const isLoading = ref(false);

const onCall = async () => {
  if (hasActiveCall.value || isLoading.value) return;
  isLoading.value = true;
  try {
    await callsStore.startOutboundCall({
      accountId: accountId.value,
      conversationId: conversationId.value,
      contactId: contactId.value,
      contactName: contactName.value,
      userToken: userToken.value,
      userId: userId.value,
    });
  } finally {
    isLoading.value = false;
  }
};

const onRequestPermission = async () => {
  if (pendingPermission.value) return;
  await callsStore.requestCallPermission({
    accountId: accountId.value,
    conversationId: conversationId.value,
    contactId: contactId.value,
  });
};
</script>

<template>
  <div v-if="isWhatsAppCloudInbox" class="flex items-center gap-2">
    <button
      v-if="!showPermissionPrompt"
      v-tooltip.top="hasActiveCall ? 'Already in a call' : 'Call via WhatsApp'"
      class="flex justify-center items-center w-8 h-8 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      :class="isCalling ? 'bg-n-amber-9' : 'bg-n-teal-9 hover:bg-n-teal-10'"
      :disabled="hasActiveCall || isLoading"
      aria-label="Call via WhatsApp"
      @click="onCall"
    >
      <i class="text-base text-white i-ph-phone-fill" />
    </button>

    <div
      v-else
      class="flex items-center gap-2 px-3 py-1 rounded-full bg-n-amber-3 border border-n-amber-7 text-xs"
    >
      <span class="text-n-amber-12">No call permission yet</span>
      <button
        class="px-2 py-0.5 rounded-full bg-n-teal-9 hover:bg-n-teal-10 text-white"
        :disabled="pendingPermission"
        @click="onRequestPermission"
      >
        {{ pendingPermission ? 'Sent…' : 'Request' }}
      </button>
    </div>
  </div>
</template>
